import BaseModel from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';

import { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

import { WarehouseResourceName } from './warehouse';

import type { IResourceName } from 'volta/models/base-model';
import type {
  ICreateWorkshopCmd,
  IDeleteWorkshopCmd,
  IUpdateManyWorkshopsCmd,
  IUpdateWorkshopCmd,
  IWorkshopCapacityExceptionCmd,
  IWorkshopCapacitySettings,
  TWorkshopType
} from './types/workshop';
import type Warehouse from './warehouse';
import type { IntlService } from 'ember-intl';

export const WorkshopResourceName: IResourceName = {
  singular: 'workshop',
  plural: 'workshops',
  table: 'workshops'
};

const DEFAULT_CAPACITY_SETTINGS: IWorkshopCapacitySettings = {
  capacityUnitFrequency: 'WEEKLY',
  capacityBufferRatio: 1
};

export const WORKSHOP_TYPES: (intl: IntlService) => TSelectOption<TWorkshopType>[] = (
  intl: IntlService
) => [
  {
    key: 'DELIVERY',
    label: intl.t('workshopTypesEnum.DELIVERY'),
    icon: 'workshop-delivery-outline',
    color: 'purple'
  },
  {
    key: 'WORKSHOP',
    label: intl.t('workshopTypesEnum.WORKSHOP'),
    icon: 'workshop-outline',
    color: 'severe'
  },
  {
    key: 'WORKSTATION',
    label: intl.t('workshopTypesEnum.WORKSTATION'),
    icon: 'workstation-outline',
    color: 'indigo'
  },
  {
    key: 'SUPPLIER',
    label: intl.t('workshopTypesEnum.SUPPLIER'),
    icon: 'storefront-outline',
    color: 'primary'
  }
];

export enum WorkshopEvents {
  WorkshopCreated = 'WorkshopCreated',
  WorkshopUpdated = 'WorkshopUpdated',
  WorkshopsUpdated = 'WorkshopsUpdated'
}

enum TWorkshopCommands {
  CreateWorkshop = 'CreateWorkshop',
  UpdateWorkshop = 'UpdateWorkshop',
  DeleteWorkshop = 'DeleteWorkshop',
  UpdateManyWorkshops = 'UpdateManyWorkshops',
  UpdateWorkshopCapacityExceptions = 'UpdateWorkshopCapacityExceptions'
}

export enum TWorkshopQueries {
  CapacityExceptions = 'capacityExceptions'
}

/**
 * Workshop ember data model
 * @class Workshop
 * @extends BaseModel
 */
export default class Workshop extends BaseModel {
  static createWorkshop = collectionCommand<ICreateWorkshopCmd, Workshop, typeof Workshop>(
    TWorkshopCommands.CreateWorkshop
  );

  static updateManyWorkshops = collectionCommand<
    IUpdateManyWorkshopsCmd,
    Workshop[],
    typeof Workshop
  >(TWorkshopCommands.UpdateManyWorkshops);

  deleteWorkshop = command<IDeleteWorkshopCmd, Workshop>(TWorkshopCommands.DeleteWorkshop);
  updateWorkshop = command<IUpdateWorkshopCmd, Workshop>(TWorkshopCommands.UpdateWorkshop);
  updateWorkshopCapacityException = command<IWorkshopCapacityExceptionCmd, Workshop>(
    TWorkshopCommands.UpdateWorkshopCapacityExceptions
  );

  @attr('string') declare code: string;
  @attr('string') description?: string;

  /**
   * The workshop is optionally linked to a warehouse
   */
  @attr('string') warehouseId?: string;
  @attr('array') workshopTypes?: TWorkshopType[];
  @attr('string') externalId?: string;
  @attr('boolean', { defaultValue: false }) isControlPoint!: boolean;
  @attr('string', { defaultValue: 'P3D' }) timeBuffer!: string;
  @attr('string') openingTime?: string;
  @attr('string') closingTime?: string;
  @attr({ defaultValue: () => ({}) }) customFields?: Record<string, any>;

  /**
   * Workshop capacity settings
   */
  @attr({ defaultValue: () => DEFAULT_CAPACITY_SETTINGS })
  capacitySettings!: IWorkshopCapacitySettings;

  /**
   * Optional warehouse
   */
  @belongsTo(WarehouseResourceName.singular, { async: false, inverse: null })
  warehouse?: Warehouse;

  @attr('array') closedWeekDays?: number[];

  /**
   * Alias for id
   */
  get workshopId() {
    return this.id;
  }

  [Type] = 'workshop' as const;
}
